import React from "react";
import { motion } from "framer-motion";
import { Target, Eye, Shield, TrendingUp, Users, Building2 } from "lucide-react";

/* Animaciones (Se mantienen igual para no romper el diseño) */
const fadeInUp = {
  initial: { opacity: 0, y: 40 },
  animate: { opacity: 1, y: 0, transition: { duration: 0.6 } },
};

const fadeInLeft = {
  initial: { opacity: 0, x: -50 },
  animate: { opacity: 1, x: 0, transition: { duration: 0.6 } },
};

const fadeInRight = {
  initial: { opacity: 0, x: 50 },
  animate: { opacity: 1, x: 0, transition: { duration: 0.6 } },
};

const staggerContainer = {
  initial: {},
  animate: {
    transition: {
      staggerChildren: 0.2
    }
  }
};

const SeccionMisionVision = () => {
  /* Cards con los NUEVOS textos del cliente */
  const cards = [
    {
      type: "misión",
      icon: Target,
      title: "Misión",
      description: "Hacer que las familias obtengan sus lotes residenciales o comerciales a precios justos.",
      features: ["Lotes residenciales", "Lotes comerciales", "Precios justos"], // Ajustado al nuevo texto
      color: "from-[#32d28a] to-[#2c976a]",
      bgColor: "bg-gradient-to-br from-[#32d28a]/20 to-[#2c976a]/20",
      delay: 0.1
    },
    {
      type: "visión",
      icon: Eye,
      title: "Visión",
      description: "Ser el proyecto más viable y atractivo de la zona, perteneciendo y creciendo de la mano con el Megaproyecto CH101.",
      features: ["Proyecto más viable", "Atractivo de la zona", "Crecimiento CH101"], // Ajustado al nuevo texto
      color: "from-[#cb4a2a] to-[#e55c3a]",
      bgColor: "bg-gradient-to-br from-[#cb4a2a]/20 to-[#e55c3a]/20",
      delay: 0.3
    }
  ];

  const valores = [
    { icon: Shield, text: "Viabilidad legal", color: "text-[#2c976a]" },
    { icon: TrendingUp, text: "Crecimiento CH101", color: "text-[#32d28a]" },
    { icon: Users, text: "Precios justos", color: "text-[#cb4a2a]" },
    { icon: Building2, text: "Lotes comerciales", color: "text-[#2c976a]" }
  ];

  return (
    <motion.section
      id="mision"
      className="relative py-16 md:py-24 lg:py-28 overflow-hidden"
      initial="initial"
      whileInView="animate"
      viewport={{ once: true, margin: "-50px" }}
      variants={staggerContainer}
    >
      {/* Fondos decorativos */}
      <div className="absolute inset-0 bg-gradient-to-br from-gray-50 to-white"></div>
      <div className="absolute top-0 left-0 w-72 h-72 bg-[#2c976a]/5 rounded-full blur-3xl"></div>
      <div className="absolute bottom-0 right-0 w-96 h-96 bg-[#cb4a2a]/5 rounded-full blur-3xl"></div>

      <div className="relative container mx-auto px-4 sm:px-6 lg:px-8">
        {/* Título de Sección */}
        <motion.div
          className="text-center mb-16 lg:mb-20"
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
        >
          <motion.div
            className="inline-flex items-center gap-2 bg-[#2c976a]/10 text-[#2c976a] text-sm font-bold px-4 py-2 rounded-full mb-4"
            variants={fadeInUp}
          >
            <Target className="w-4 h-4" />
            COMPROMISO CHANCAY
          </motion.div>

          <motion.h2
            className="text-4xl md:text-5xl lg:text-6xl font-extrabold mb-6"
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
          >
            <span className="text-gray-900">Nuestra</span>
            <br />
            <span className="text-[#2c976a]">Misión</span>
            <span className="text-gray-900"> y </span>
            <span className="text-[#cb4a2a]">Visión</span>
          </motion.h2>
        </motion.div>

        {/* Cards de Misión y Visión */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 lg:gap-12 max-w-6xl mx-auto mb-16 lg:mb-20">
          {cards.map((card, index) => {
            const IconComponent = card.icon;
            return (
              <motion.div
                key={card.type}
                className={`relative rounded-3xl p-8 lg:p-10 shadow-2xl border border-gray-200/50 backdrop-blur-sm ${card.bgColor}`}
                variants={index === 0 ? fadeInLeft : fadeInRight}
                whileHover={{ 
                  y: -8, 
                  scale: 1.02,
                  transition: { type: "spring", stiffness: 300 }
                }}
                transition={{ delay: card.delay }}
              >
                <motion.div
                  className={`inline-flex p-5 rounded-2xl bg-gradient-to-r ${card.color} text-white shadow-lg mb-6`}
                  whileHover={{ scale: 1.1, rotate: 5 }}
                >
                  <IconComponent className="w-8 h-8" />
                </motion.div>

                <motion.h3 className="text-2xl lg:text-3xl font-bold text-gray-900 mb-4">
                  {card.title}
                </motion.h3>

                <motion.p className="text-lg text-gray-700 leading-relaxed mb-6 italic">
                  "{card.description}"
                </motion.p>

                <div className="space-y-3">
                  {card.features.map((feature, featureIndex) => (
                    <motion.div
                      key={featureIndex}
                      className="flex items-center gap-3 text-gray-800 font-medium"
                    >
                      <div className={`w-2 h-2 rounded-full bg-gradient-to-r ${card.color}`}></div>
                      <span>{feature}</span>
                    </motion.div>
                  ))}
                </div>
              </motion.div>
            );
          })}
        </div>

        {/* Sección de Valores (Opcional, se mantiene por estética) */}
        <motion.div className="text-center">
          <motion.h3 className="text-3xl md:text-4xl font-bold text-gray-900 mb-12">
            Nuestra <span className="text-[#2c976a]">Viabilidad</span>
          </motion.h3>

          <div className="grid grid-cols-2 md:grid-cols-4 gap-6 max-w-4xl mx-auto">
            {valores.map((valor, index) => {
              const ValorIcon = valor.icon;
              return (
                <motion.div
                  key={index}
                  className="flex flex-col items-center text-center p-6 bg-white rounded-2xl shadow-lg border border-gray-200/50"
                  variants={fadeInUp}
                  whileHover={{ y: -5 }}
                >
                  <div className="bg-gradient-to-br from-[#2c976a] to-[#32d28a] p-4 rounded-2xl mb-4 text-white">
                    <ValorIcon className="w-6 h-6" />
                  </div>
                  <span className={`font-semibold text-sm ${valor.color}`}>
                    {valor.text}
                  </span>
                </motion.div>
              );
            })}
          </div>
        </motion.div>

        {/* TEXTO DE CIERRE DEL CLIENTE */}
        <motion.div
          className="text-center mt-16 lg:mt-20"
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ delay: 0.9 }}
        >
          <motion.p className="text-xl md:text-2xl text-gray-700 mb-8 max-w-2xl mx-auto leading-relaxed">
            <span className="text-[#2c976a] font-bold">Confía en la viabilidad de nuestro proyecto</span> y haz realidad el sueño de tu terreno propio.
          </motion.p>
        </motion.div>
      </div>
    </motion.section>
  );
};

export default SeccionMisionVision;